import { isObject } from "underscore";
import {
	LanguagesAbbr,
	ModuleGroupingTypes,
	ModuleGroups,
	PageModuleGroupModules,
	Session,
	SessionTabsLayoutTypes,
	TranslateString,
} from "types/working-model";
import { TranslateFunction } from "i18n/useTranslationModules";
import { MODULE_GROUP_DISPLAY_NAMES } from "utils/module-groups";

// ATF, we never show group module titles
// BTF:
// - show group module title title for overview modules
// - show group module title if there are ANY active modules

const getModuleSectionTitle = (session: Session, language: LanguagesAbbr, moduleGroup: PageModuleGroupModules | undefined, t: TranslateFunction): string => {
	// if mnoduleGroup is not on, don't show
	if (!moduleGroup?.is_on) return '';

	// if no group modules, no need to show the title so immediately return
	if (!moduleGroup?.modules?.length) return '';

	// never show titles in ATF, module groups in ATF have nested pills
	const isAboveTheFold = !!(session?.layout?.tabs?.layout === SessionTabsLayoutTypes.AboveTheFold);
	if (isAboveTheFold) return '';

	// rest of the code is for BTF

	const name = getModuleGroupTitle(moduleGroup, language);
	// if no module group name, immediately return
	if (!name.length) return '';

	// some groups are labeled differently for attendees than they are internally, matching the nav buttons
	const displayName = getModuleGroupDisplayName(moduleGroup, name, language, t);

	// if overview module, we always show the title
	const isOverviewModule = moduleGroup?.type === ModuleGroupingTypes.Overview;
	if (isOverviewModule) return displayName;

	// if no active modules for the group, don't show the title
	const activeModules = getActiveModulesForGroup(moduleGroup, session);
	if (!activeModules.length) return '';

	// if we have active modules we always return the module group title for BTF, regardless if it's 1 or 100 modules
	return displayName;
};

export default getModuleSectionTitle;

export const getModuleGroupTitle = (moduleGroup: PageModuleGroupModules, language: LanguagesAbbr) => {
	let name = '';
	const moduleGroupName = moduleGroup?.name;
	if (moduleGroupName) {
		if (isObject(moduleGroupName)) {
			name = (moduleGroupName?.[language] ?? moduleGroupName?.base ?? '') as string;
		} else {
			name = moduleGroupName ?? '';
		}
	}

	return name.trim();
};

// Some groups are labeled differently for attendees than they are internally, and the group name saved on the
// event is a translation of the internal name (Engage is saved as "Comprometer" in Spanish), so the localized
// label has to come from the session namespace, which is keyed by the internal base name (Engage -> Momentos).
// The base name is only replaced once an admin renames the group, so this never overrides a custom name.
export const getModuleGroupDisplayName = (
	moduleGroup: PageModuleGroupModules,
	name: string,
	language: LanguagesAbbr,
	t: TranslateFunction
): string => {
	const moduleGroupName = moduleGroup?.name;
	if (!isObject(moduleGroupName)) return name;

	// an admin who translated this group themselves gets their own wording for that language
	const languageOverrides = (moduleGroupName as TranslateString)?.overrides;
	if (Array.isArray(languageOverrides) && languageOverrides.includes(language)) return name;

	const displayName = MODULE_GROUP_DISPLAY_NAMES[moduleGroupName?.base as ModuleGroups];
	if (!displayName) return name;

	// fall back to the untranslated display name so languages without the key still get the right label
	return t(moduleGroupName.base, displayName);
};

export const getActiveModulesForGroup = (moduleGroup: PageModuleGroupModules, session: Session): number[] => {
	return (moduleGroup?.modules || []).filter(group_module_id => {
		const matchingModule = session?.modules.find(session_module => session_module.id === group_module_id);

		return matchingModule
			? matchingModule.is_on && !!matchingModule.content_modules?.length
			: false;
	});
};
